// src/pages/EditarFraccionamiento.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, functions } from '../firebase';
import { doc, getDoc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import './EditarFraccionamiento.css';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';
import PlatformBackButton from '../components/PlatformBackButton';

const EditarFraccionamiento = () => {
    const { fraccId } = useParams();
    const navigate = useNavigate();
    const { userData, currentUser } = useAuth();
    
    const [formData, setFormData] = useState({ id: null, nombre: '' });
    const [loading, setLoading] = useState(true);
    const [adminData, setAdminData] = useState({ id: null, nombre: '' });
    const [downloadingBackup, setDownloadingBackup] = useState(false);
    const { showNotification } = useNotification();

    const userRole = (userData?.rol || userData?.role || '').toLowerCase();
    const isPlatformAdmin = ['platformadmin', 'superadmin', 'plataforma'].includes(userRole);

    // 1. LÓGICA DE OBTENCIÓN DE DATOS SIMPLIFICADA Y CORRECTA
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const fraccDocRef = doc(db, 'fraccionamientos', fraccId);
            const fraccDoc = await getDoc(fraccDocRef);

            if (fraccDoc.exists()) {
                const data = fraccDoc.data();
                const fechaVencimiento = data.fechaVencimiento?.toDate().toISOString().split('T')[0] || '';
                setFormData({ ...data, fechaVencimiento });
                setAdminData({ id: data.adminId, nombre: data.adminNombre });
            } else {
                showNotification('El fraccionamiento no fue encontrado.', 'error');
                navigate('/fraccionamientos');
            }
        } catch (error) {
            console.error("Error al obtener datos:", error);
            showNotification('Error al cargar la información.', 'error');
        } finally {
            setLoading(false);
        }
    }, [fraccId, navigate, showNotification]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFraccChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAdminChange = (e) => {
        // En el input del admin, el 'name' es 'nombre', actualiza solo el nombre
        setAdminData({ ...adminData, nombre: e.target.value });
    };

    const handleDownloadBackup = async () => {
        if (!fraccId) {
            showNotification('No se identificó el ID del fraccionamiento.', 'error');
            return;
        }
        setDownloadingBackup(true);
        try {
            const exportarFn = httpsCallable(functions, 'exportarDatosFraccionamiento');
            const res = await exportarFn({ fraccionamientoId: fraccId });

            if (res.data?.urlDescarga) {
                const nombreLimpio = (formData.nombre || 'fraccionamiento').replace(/[^a-zA-Z0-9_-]/g, '_');
                const fechaHoy = new Date().toISOString().slice(0, 10);
                const enlace = document.createElement('a');
                enlace.href = res.data.urlDescarga;
                enlace.download = `backup_${nombreLimpio}_${fechaHoy}.json.gz`;
                enlace.target = '_blank';
                document.body.appendChild(enlace);
                enlace.click();
                document.body.removeChild(enlace);

                showNotification('¡Respaldo descargado exitosamente!', 'success');
            } else {
                throw new Error('No se recibió la URL del archivo de respaldo.');
            }
        } catch (error) {
            console.error('Error al descargar respaldo:', error);
            showNotification(`Error al exportar respaldo: ${error.message || error}`, 'error');
        } finally {
            setDownloadingBackup(false);
        }
    };
    
    // 2. LÓGICA DE GUARDADO FORTALECIDA PARA MANTENER LA CONSISTENCIA
    const handleSaveChanges = async () => {
        if (!adminData.id || !formData) {
            showNotification("Error: Faltan datos para guardar.", 'error');
            return;
        }
        setLoading(true);
        try {
            const batch = writeBatch(db);

            // Actualiza el documento del fraccionamiento (incluyendo el nombre desnormalizado)
            const fraccDocRef = doc(db, 'fraccionamientos', fraccId);
            batch.update(fraccDocRef, {
                nombre: formData.nombre,
                estado: formData.estado,
                suscripcion: formData.suscripcion,
                fechaVencimiento: new Date(formData.fechaVencimiento),
                adminNombre: adminData.nombre // <-- Mantiene la consistencia de los datos
            });

            // Actualiza el documento del administrador en 'usuarios'
            const adminDocRef = doc(db, 'usuarios', adminData.id);
            batch.update(adminDocRef, {
                nombre: adminData.nombre,
            });

            await batch.commit();

            showNotification('¡Cambios guardados con éxito!', 'success');
            try {
                await registerMovement({
                    fraccionamientoId: fraccId,
                    titulo: 'Datos del fraccionamiento actualizados',
                    descripcion: `${formData.nombre} · Estado: ${formData.estado}`,
                    categoria: 'Configuración',
                    tipoMovimiento: 'fraccionamiento_actualizado',
                    origen: 'fraccionamientos',
                    metadata: {
                        nombre: formData.nombre,
                        estado: formData.estado,
                        suscripcion: formData.suscripcion,
                        fechaVencimiento: formData.fechaVencimiento ? new Date(formData.fechaVencimiento) : null,
                        adminId: adminData.id,
                        adminNombre: adminData.nombre,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('EditarFraccionamiento: error al registrar movimiento', logError);
            }
            navigate('/fraccionamientos');
        } catch (error) {
            console.error("Error al guardar cambios:", error);
            showNotification('Hubo un error al guardar los cambios.', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <p>Cargando...</p>;
    if (!formData) return <p>No se encontró el fraccionamiento.</p>;

    return (
        <div className="edit-fracc-container">
            <div className="edit-fracc-header">
                <PlatformBackButton to="/fraccionamientos" />
                <h2>Editando: {formData.nombre}</h2>
            </div>
            <div className="edit-form-grid">
                <div className="edit-card">
                    <h3>Información General</h3>
                    <div className="form-group">
                        <label>Nombre del Fraccionamiento</label>
                        <input type="text" name="nombre" value={formData.nombre} onChange={handleFraccChange} />
                    </div>
                    <div className="form-group">
                        <label>Estado</label>
                        <select name="estado" value={formData.estado} onChange={handleFraccChange}>
                            <option value="Activo">Activo</option>
                            <option value="Inactivo">Inactivo</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Nombre del Administrador Principal</label>
                        <input type="text" name="nombre" value={adminData.nombre} onChange={handleAdminChange} />
                    </div>
                </div>
                <div className="edit-card">
                    <h3>Suscripción</h3>
                    <div className="form-group">
                        <label>Plan de Suscripción</label>
                        <select name="suscripcion" value={formData.suscripcion} onChange={handleFraccChange}>
                            <option value="Demo">Demo</option>
                            <option value="Basica">Basica</option>
                            <option value="Estándar">Estándar</option>
                            <option value="Premium">Premium</option>
                            <option value="Premium-Plus">Premium-Plus</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Fecha de Vencimiento</label>
                        <input type="date" name="fechaVencimiento" value={formData.fechaVencimiento} onChange={handleFraccChange} />
                    </div>
                </div>
                {isPlatformAdmin && (
                    <div className="edit-card backup-card">
                        <div className="backup-card-header">
                            <h3>BackUp</h3>
                            <span className="backup-badge-zen">RPO ≤ 24h</span>
                        </div>
                        <p className="backup-card-description">
                            Respaldo integral de la base de datos del fraccionamiento (finanzas, cobranza, casas, accesos y comunidad).
                        </p>
                        <div className="backup-info-grid">
                            <div className="backup-info-row">
                                <span className="backup-info-title">Frecuencia:</span>
                                <span className="backup-info-detail">Diaria (02:00 hrs CST)</span>
                            </div>
                            <div className="backup-info-row">
                                <span className="backup-info-title">Retención:</span>
                                <span className="backup-info-detail">30 días naturales</span>
                            </div>
                            <div className="backup-info-row">
                                <span className="backup-info-title">RTO Objetivo:</span>
                                <span className="backup-info-detail">4 a 8 hrs (por comunidad)</span>
                            </div>
                            <div className="backup-info-row">
                                <span className="backup-info-title">Privacidad LFPDPPP:</span>
                                <span className="backup-info-detail">Fotografías sensibles excluidas</span>
                            </div>
                        </div>
                        <div className="backup-btn-wrapper">
                            <button
                                type="button"
                                className="btn-export-backup"
                                onClick={handleDownloadBackup}
                                disabled={downloadingBackup || loading}
                            >
                                {downloadingBackup ? (
                                    <>
                                        <span className="backup-inline-spinner"></span>
                                        <span>Generando respaldo...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        <span>Descargar Respaldo ({formData.nombre || 'Comunidad'})</span>
                                    </>
                                )}
                            </button>
                            <small className="backup-format-caption">Archivo comprimido .json.gz listo para auditoría o restauración</small>
                        </div>
                    </div>
                )}
            </div>
            <div className="save-actions">
                <button onClick={handleSaveChanges} disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>
        </div>
    );
};

export default EditarFraccionamiento;